'use client'

import SearchIcon from '@mui/icons-material/Search'
import {
  Divider,
  InputAdornment,
  ListSubheader,
  MenuItem,
  MenuList,
  TextField,
} from '@mui/material'
import type { MenuProps } from '@mui/material/Menu'
import type { MenuListProps } from '@mui/material/MenuList'
import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

function componentName(type: unknown): string {
  if (typeof type === 'string') {
    return type
  }
  if (
    typeof type !== 'function' &&
    (typeof type !== 'object' || type === null)
  ) {
    return ''
  }
  const typed = type as {
    displayName?: string
    name?: string
    muiName?: string
    type?: unknown
  }
  return typed.muiName || typed.displayName || typed.name || ''
}

function isComponentType(
  element: ReactElement,
  component: unknown,
  name: string,
): boolean {
  if (element.type === component) {
    return true
  }
  return componentName(element.type).includes(name)
}

function getNodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') {
    return ''
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join(' ')
  }
  if (isValidElement(node)) {
    return getNodeText((node.props as { children?: ReactNode }).children)
  }
  return ''
}

function isListSubheaderElement(element: ReactElement): boolean {
  return isComponentType(element, ListSubheader, 'ListSubheader')
}

function isDividerElement(element: ReactElement): boolean {
  return isComponentType(element, Divider, 'Divider')
}

function itemMatchesQuery(element: ReactElement, query: string): boolean {
  const text = getNodeText(element).toLowerCase()
  const value = (element.props as { value?: unknown }).value
  const valueText = value == null ? '' : String(value).toLowerCase()
  return text.includes(query) || valueText.includes(query)
}

export function filterSelectChildren(
  children: ReactNode,
  query: string,
): ReactNode[] {
  const items = Children.toArray(children)
  const normalized = query.trim().toLowerCase()
  if (!normalized) {
    return items
  }

  const result: ReactNode[] = []
  let pendingHeader: ReactNode | null = null

  for (const child of items) {
    if (!isValidElement(child)) {
      continue
    }
    if (isListSubheaderElement(child)) {
      pendingHeader = child
      continue
    }
    if (isDividerElement(child)) {
      if (result.length > 0) {
        result.push(child)
      }
      continue
    }
    if (!itemMatchesQuery(child, normalized)) {
      continue
    }
    if (pendingHeader) {
      result.push(pendingHeader)
      pendingHeader = null
    }
    result.push(child)
  }

  return result
}

export function searchableSelectMenuProps(
  overrides?: Partial<MenuProps>,
): Partial<MenuProps> {
  const overrideListSlotProps =
    overrides?.slotProps?.list && typeof overrides.slotProps.list !== 'function'
      ? overrides.slotProps.list
      : undefined

  return {
    autoFocus: false,
    disableAutoFocusItem: true,
    variant: 'menu',
    ...overrides,
    slots: {
      ...overrides?.slots,
      list: SearchableMenuList,
    },
    slotProps: {
      ...overrides?.slotProps,
      list: {
        autoFocusItem: false,
        ...overrideListSlotProps,
      },
    },
  }
}

export function SearchableMenuList(props: MenuListProps) {
  const { children, autoFocusItem: _autoFocusItem, ...other } = props
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const filteredChildren = useMemo(
    () => filterSelectChildren(children, query),
    [children, query],
  )

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [])

  return (
    <MenuList {...other} autoFocusItem={false} component="ul">
      <ListSubheader className="searchable-select-filter">
        <TextField
          size="small"
          fullWidth
          placeholder="Search..."
          value={query}
          inputRef={inputRef}
          onChange={(event) => setQuery(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') {
              event.stopPropagation()
            }
          }}
          slotProps={{
            htmlInput: {
              'aria-label': 'Search options',
              autoComplete: 'off',
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      </ListSubheader>
      {filteredChildren.length > 0 ? (
        filteredChildren
      ) : query.trim() ? (
        <MenuItem disabled>No matches</MenuItem>
      ) : null}
    </MenuList>
  )
}
